import React from 'react'
import { cva } from 'class-variance-authority'
import Icon from '@/components/Icon'
import { cn } from '@/lib/utils'
import type { ButtonProps } from './Button/Button.type'
import LoadingSpinner from './LoadingSpinner'

const buttonVariants = cva(
  'flex flex-row items-center justify-center gap-2 cursor-pointer',
  {
    variants: {
      variant: {
        primary:
          'bg-accent hover:bg-accent-light border-transparent text-secondary font-bold rounded-md',
        secondary:
          'bg-secondary hover:bg-secondary-dark border-solid border-primary text-white font-700 rounded-md',
        tertiary:
          'bg-transparent hover:bg-transparent-30 border-transparent text-white hover:text-accent rounded-md',
        destructive:
          'bg-error hover:bg-error-dark border-transparent text-white rounded-md',
      },
      fullWidth: {
        true: 'w-full',
      },
      inverted: {
        true: 'text-white',
      },
      active: {
        true: '',
      },
      disabled: {
        true: 'opacity-50 cursor-not-allowed!',
      },
      iconOnly: {
        true: 'px-2 border-transparent',
        false: 'px-4',
      },
      size: {
        sm: 'max-h-8 py-1',
        md: 'max-h-12 py-2',
        lg: 'max-h-12 py-3',
        xlg: 'max-h-16 py-4',
      },
    },
    compoundVariants: [
      {
        variant: 'primary',
        active: true,
        className: 'ring-1 ring-offset-1 ring-secondary',
      },
      {
        variant: 'secondary',
        active: true,
        className: 'ring-1 ring-offset-1 ring-secondary',
      },
      {
        variant: 'tertiary',
        active: true,
        className: 'text-active hover:text-active bg-primary',
      },
      {
        variant: 'primary',
        active: true,
        className: 'bg-accent-light',
      },
      {
        variant: 'secondary',
        active: true,
        className: 'bg-secondary-dark',
      },
      {
        iconOnly: true,
        size: 'md',
        className: 'p-2',
      },
      {
        iconOnly: true,
        size: 'lg',
        className: 'text-lg p-8 max-h-16',
      },
      {
        iconOnly: true,
        size: 'xlg',
        className: 'text-5xl p-16 max-h-32',
      },
      // Prevent hover effects when disabled
      {
        variant: 'primary',
        disabled: true,
        className: 'hover:bg-accent',
      },
      {
        variant: 'secondary',
        disabled: true,
        className: 'hover:bg-secondary',
      },
      {
        variant: 'tertiary',
        disabled: true,
        className: 'hover:bg-transparent hover:text-white',
      },
      {
        variant: 'destructive',
        disabled: true,
        className: 'hover:bg-error',
      },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      inverted: false,
      active: false,
      disabled: false,
      fullWidth: false,
      iconOnly: false,
    },
  }
)

/**
 * ```
 * import { Button } from '@/components/Button'
 *
 * <Button
 *  label='Click me'
 *  onClick={() => console.log('Button clicked')}
 * />
 * ```
 * A standard button component that can be used to trigger actions in the application.
 *
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      // Core props
      onClick,
      type = 'button',
      disabled = false,
      'data-testid': testId,

      // Icon props
      iconProps,
      iconPosition = 'start',
      loading = false,

      // Content props
      label,

      // Style props
      variant = 'primary',
      size = 'md',
      active = false,
      inverted = false,
      fullWidth = false,
      className,

      // Accessibility props
      'aria-label': ariaLabel,
      'aria-expanded': ariaExpanded,
      'aria-pressed': ariaPressed,

      role,

      ...props
    },
    ref
  ) => {
    // Button is icon-only if it has an icon but no label, or if it's in loading state
    const isIconOnly = (!!iconProps?.icon && !label) || loading

    const buttonClasses = cn(
      buttonVariants({
        variant,
        size,
        fullWidth,
        active,
        inverted,
        disabled,
        iconOnly: isIconOnly,
      }),
      className
    )

    return (
      <button
        ref={ref}
        onClick={onClick}
        type={type}
        disabled={disabled || loading}
        className={buttonClasses}
        data-testid={testId}
        aria-label={
          ariaLabel ??
          (isIconOnly && iconProps?.icon
            ? Array.isArray(iconProps.icon)
              ? iconProps.icon.join(' ')
              : typeof iconProps.icon === 'string'
                ? iconProps.icon
                : 'Icon button'
            : loading
              ? 'Loading...'
              : undefined)
        }
        aria-expanded={ariaExpanded}
        aria-pressed={active ? 'true' : ariaPressed}
        role={role}
        {...props}
      >
        {loading ? (
          <LoadingSpinner inverted={inverted} />
        ) : (
          <>
            {!!iconProps?.icon && iconPosition === 'start' && (
              <Icon {...iconProps} />
            )}
            {!!label && <span className='button-label'>{label}</span>}
            {!!iconProps?.icon && iconPosition === 'end' && (
              <Icon {...iconProps} />
            )}
          </>
        )}
      </button>
    )
  }
)

Button.displayName = 'Button'

export { Button }
export default Button
